<template>
    <!-- MBR085 Find Branches — member_app_UI/song_fa_members_find_a_branch_mobile -->
    <div class="page-header align-items-start min-vh-100 mbr-branches">
        <span class="mask bg-white"></span>
        <div class="container p-0">
            <!-- the design's compass button is dropped: branches have no coordinates -->
            <header class="mbr-branches__bar">
                <button type="button" class="mbr-branches__back" :aria-label="$t('common.back')" @click="goBack()">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
                    </svg>
                </button>
                <h1 class="mbr-branches__title">{{ $t('stores.title') }}</h1>
            </header>

            <main class="mbr-branches__body">
                <label class="mbr-branches__search">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        class="mbr-branches__search-input"
                        :placeholder="$t('stores.search_placeholder')"
                        :aria-label="$t('stores.search_placeholder')"
                        :value="searchKeyword"
                        @input="onChangesearchKeyword"
                    />
                </label>

                <template v-if="isLoadDone">
                    <!-- MBR086 the design's "Corporate HQ" card. The member API has no HQ address or
                         phone, so the tinted card carries the merchant's free text instead. -->
                    <section v-if="freeText" class="mbr-branches__note">{{ freeText }}</section>

                    <ul v-if="branchCards.length > 0" class="mbr-branches__list">
                        <li v-for="card in branchCards" :key="cardKey(card)" class="mbr-branch">
                            <div class="mbr-branch__row">
                                <div class="mbr-branch__info">
                                    <span v-if="card.status" class="mbr-branch__badge" :class="`mbr-branch__badge--${card.status}`">
                                        <span class="mbr-branch__dot" aria-hidden="true"></span>
                                        {{ statusLabels[card.status] }}
                                    </span>
                                    <p class="mbr-branch__name">{{ card.branch.branch_name }}</p>
                                    <p v-if="card.branch.address" class="mbr-branch__address">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                        <span>{{ card.branch.address }}</span>
                                    </p>
                                    <p v-if="card.summary" class="mbr-branch__today" :class="{ 'mbr-branch__today--strong': card.summary.strong }">
                                        {{ card.summary.text }}
                                    </p>
                                    <button
                                        v-if="card.days.length > 1 && !isExpanded(card)"
                                        type="button"
                                        class="mbr-branch__hours-toggle"
                                        :aria-expanded="false"
                                        @click="toggleHours(card)"
                                    >
                                        <span>{{ $t('stores.opening_hours') }}</span>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    class="mbr-branch__order"
                                    :disabled="card.status === 'closed-today'"
                                    @click="gotoOrderingOnlineByBranch(card.branch)"
                                >
                                    {{ $t('stores.order') }}
                                </button>
                            </div>

                            <div v-if="card.days.length > 1 && isExpanded(card)" class="mbr-branch__hours">
                                <button
                                    type="button"
                                    class="mbr-branch__hours-head"
                                    :aria-expanded="true"
                                    @click="toggleHours(card)"
                                >
                                    <span>{{ $t('stores.opening_hours') }}</span>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                        <polyline points="18 15 12 9 6 15" />
                                    </svg>
                                </button>
                                <dl class="mbr-branch__days">
                                    <div
                                        v-for="day in card.days"
                                        :key="day.day"
                                        class="mbr-branch__day"
                                        :class="{ 'mbr-branch__day--today': day === card.today }"
                                    >
                                        <dt>{{ day === card.today ? $t('stores.day_today', { day: dayName(day.day) }) : dayName(day.day) }}</dt>
                                        <dd>{{ day.label }}</dd>
                                    </div>
                                </dl>
                            </div>
                        </li>
                    </ul>
                    <p v-else class="mbr-branches__empty">{{ $t('stores.no_branches') }}</p>
                </template>
            </main>
        </div>
        <div class="position-fixed top-1 end-1 z-index-2">
            <material-snackbar v-if="snackbar === 'danger' || snackbar === 'success'" title="" date=""
                :description="message" :icon="{ component: 'campaign', color: 'white' }" :color="snackbar"
                :close-handler="closeSnackbar" />
        </div>
        <material-loading :active="loaderActive" />
    </div>

</template>

<script>
import { mapMutations } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MerchantService from "../services/merchant.service";
import 'bootstrap/dist/js/bootstrap.bundle'
import MaterialLoading from "@/components/MaterialLoading.vue";
import 'bootstrap/js/dist/modal';
import CryptoJS from 'crypto-js';
import TokenService from "../services/token.service";
import { localStorageName, getMerchantCode } from "@/config/utils";
import { errorMessage } from "@/utils/errorMessage";

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// find-branch sends "Open All Day", "Closed" or "08:00 - 17:00 PM, 18:00 - 22:00 PM"
// (the end time carries a stray 24h AM/PM suffix)
const toMinutes = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
};

const parseRanges = (time) =>
    (time.match(/\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}/g) || []).map((range) => {
        const [start, end] = range.split("-").map((t) => toMinutes(t.trim()));
        return { start, end };
    });

const formatTime = (time) => {
    if (time === "Open All Day") return "Open all day";
    return time.replace(/\s*(AM|PM)\b/gi, "").replace(/\s*-\s*/g, "–");
};

// "unknown" (no hours configured) shows no badge and keeps Order enabled
const dayStatus = (day, now) => {
    if (!day) return null;
    if (day.time === "Open All Day") return "open";
    const ranges = parseRanges(day.time);
    if (!ranges.length) return day.time === "Closed" ? "closed-today" : null;
    const minutes = now.getHours() * 60 + now.getMinutes();
    const isOpen = ranges.some(({ start, end }) =>
        end > start ? minutes >= start && minutes < end : minutes >= start || minutes < end
    );
    return isOpen ? "open" : "closed-now";
};

export default {
    name: "find-branch",
    components: {
        MaterialSnackbar,
        MaterialLoading,
    },
    data() {
        return {
            isLoadDone: false,
            snackbar: null,
            message: "",
            loaderActive: false,
            merchantCode: "",
            branches: [],
            freeText: '',
            searchKeyword: '',
            searchTimerId: null,
            expanded: {},
        };
    },
    computed: {
        statusLabels() {
            return {
                open: this.$t("stores.open_now"),
                "closed-now": this.$t("stores.closed_now"),
                "closed-today": this.$t("stores.closed_today"),
            };
        },
        branchCards() {
            const now = new Date();
            return this.branches.map((branch) => {
                const days = (branch.operation_days_text || []).map((day) => ({
                    ...day,
                    label: this.timeLabel(day.time),
                }));
                const everyDay = days.length === 1 && days[0].day === "Every day";
                const today = everyDay
                    ? days[0]
                    : days.find((day) => day.selected) || days.find((day) => day.day === DAY_SHORT[now.getDay()]);
                const status = dayStatus(today, now);
                let summary = null;
                if (everyDay) {
                    summary = { text: this.$t("stores.every_day_hours", { hours: today.label }), strong: false };
                } else if (today && status !== "closed-today") {
                    summary = { text: `${this.dayName(today.day)} ${today.label}`, strong: true };
                }
                return { branch, days, today, status, summary };
            });
        },
    },
    async created() {
        this.isLoadDone = false;
        await this.getBranches(this.searchKeyword);
        this.isLoadDone = true;
    },
    // draws its own header like the design: hide the top navbar, and keep the
    // sidenav as an off-screen drawer (same as Invite Friends)
    beforeMount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.add("mbr-sidenav-overlay");
        app.classList.remove("g-sidenav-pinned");
    },
    mounted() {
        const code = getMerchantCode();
        this.merchantCode = code ? `/${code}` : ''
    },
    beforeUnmount() {
        clearTimeout(this.searchTimerId);
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.remove("mbr-sidenav-overlay");
        if (window.innerWidth > 1200) {
            app.classList.add("g-sidenav-pinned");
        }
    },
    methods: {
        ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
        // find-branch sends English day names / "Open All Day" / "Closed"; the raw values stay
        // for the status logic above, only the displayed text is translated
        dayName(day) {
            const index = DAY_SHORT.indexOf(day);
            if (index !== -1) return this.$t(`stores.day_${DAY_SHORT[index].toLowerCase()}`);
            if (day === "Every day") return this.$t("stores.every_day");
            return day;
        },
        timeLabel(time) {
            if (time === "Open All Day") return this.$t("stores.open_all_day");
            if (time === "Closed") return this.$t("stores.closed");
            return formatTime(time);
        },
        cardKey(card) {
            return card.branch.id || card.branch.branch_code;
        },
        isExpanded(card) {
            return !!this.expanded[this.cardKey(card)];
        },
        toggleHours(card) {
            const key = this.cardKey(card);
            this.expanded = { ...this.expanded, [key]: !this.expanded[key] };
        },
        goBack() {
            this.$router.push(this.merchantCode + "/account");
        },
        closeSnackbar() {
            this.snackbar = null;
        },

        setTimeOutNotification() {
            setTimeout(() => { this.snackbar = null; }, 3000);
        },

        onChangesearchKeyword(event) {
            this.searchKeyword = event.target.value
            clearTimeout(this.searchTimerId);
            this.searchTimerId = setTimeout(() => { this.getBranches(this.searchKeyword) }, 1000)
        },

        async getBranches(search_keyword = '') {
            search_keyword = this.searchKeyword
            this.showLoader()
            await MerchantService.getBranches(search_keyword).then(
                (response) => {
                    if (response.data.result.isSuccess) {
                        this.branches = response.data.data.branches
                        this.freeText = response.data.data.freeText
                    } else {
                        this.snackbar = 'danger'
                        this.message = errorMessage(response.data.result.message)
                        this.setTimeOutNotification()
                    }
                    this.hideLoader()
                },
                (error) => {
                    this.snackbar = 'danger'
                    this.message = errorMessage(error)
                    this.hideLoader()
                    this.setTimeOutNotification()
                }
            )
        },

        showLoader() {
            this.loaderActive = true;
        },
        hideLoader() {
            this.loaderActive = false;
        },
        encryptData() {
            const user = JSON.parse(localStorage.getItem(localStorageName("member")));
            if(user && user.data && user.data.user && user.data.user.phone_number) {
                var data = {
                    phone_number: user.data.user.phone_number,
                    passcode: this.decryptData(localStorage.getItem(localStorageName("MEMBER_PASSCODE")))
                }
                if (data) {
                    const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), process.env.VUE_APP_KEY_ENCRYPT_MEMBER_INFORMATION).toString();
                    return btoa(encryptedData);
                }
            } else {
                return "";
            }
        },
        // decryptData(encryptedData) {
        //     if (encryptedData) {
        //         try {
        //             const decryptedData = CryptoJS.AES.decrypt(atob(encryptedData), process.env.VUE_APP_KEY_ENCRYPT_MEMBER_INFORMATION).toString(CryptoJS.enc.Utf8);
        //             return decryptedData;
        //         } catch (error) {
        //             return "";
        //         }
        //     }
        // },
        decryptData(encryptedData) {
            if (encryptedData) {
                try {
                    const decryptedData = CryptoJS.AES.decrypt(encryptedData, process.env.VUE_APP_KEY_ENCRYPT_MEMBER_INFORMATION).toString(CryptoJS.enc.Utf8);
                    return decryptedData;
                } catch (error) {
                    return "";
                }
            }
        },
        gotoOrderingOnlineByBranch(item) {
            var memberInformation = this.encryptData();
            if(memberInformation) {
                const token = TokenService.getLocalAccessToken();
                var memberLogin = TokenService.getMember();
                window.open(process.env.VUE_APP_ONLINE_ORDERING_BY_BRANCH_URL+this.merchantCode+"/"+item.branch_code+"?key="+token+"&memberId="+ memberLogin?.data.user.id, '_blank');
            }
        }
    },
};
</script>
<style scoped>
/* MBR085 Find Branches. The old block was unscoped; `.text-title`, `.text-custom` and
   `.font-weight-bold` are used by other screens, so they moved to custom.css verbatim. */
.mbr-branches.page-header {
    overflow: visible;
    overflow-x: clip;
    font-family: var(--mbr-font-body);
    color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }` */
.mbr-branches :where(:not(.material-icons-round)) {
    font-family: var(--mbr-font-body);
}

.mbr-branches .mask {
    background: var(--mbr-color-canvas) !important;
    opacity: 1;
}

.mbr-branches .container {
    max-width: 440px;
    margin: 0 auto;
}

.mbr-branches p {
    margin: 0;
}

/* header */
.mbr-branches__bar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 24px 24px 12px;
    background: var(--mbr-nav-bg);
    backdrop-filter: blur(var(--mbr-nav-blur));
    -webkit-backdrop-filter: blur(var(--mbr-nav-blur));
}

.mbr-branches__back {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    background: var(--mbr-color-surface);
    border: 1px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
    color: var(--mbr-color-ink-headline);
    transition: background-color 0.15s ease;
}

.mbr-branches__back:hover {
    background: var(--mbr-color-tint);
}

.mbr-branches__back svg {
    width: 18px;
    height: 18px;
}

.mbr-branches__title {
    margin: 0;
    font-family: var(--mbr-font-headline) !important;
    font-size: 24px;
    font-weight: 800;
    line-height: 32px;
    letter-spacing: -0.02em;
    color: var(--mbr-color-ink-headline);
}

.mbr-branches__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 4px 20px 40px;
}

/* search pill */
.mbr-branches__search {
    display: flex;
    align-items: center;
    gap: 12px;
    height: 48px;
    margin: 0;
    padding: 0 16px;
    background: var(--mbr-color-tint-dark);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
    border-radius: var(--mbr-radius-full);
    box-shadow: inset 0 2px 4px rgba(var(--mbr-color-shadow-rgb), 0.05);
    color: var(--mbr-color-ink-muted);
    cursor: text;
}

.mbr-branches__search:focus-within {
    border-color: var(--mbr-color-primary);
}

.mbr-branches__search svg {
    flex: none;
    width: 18px;
    height: 18px;
    color: var(--mbr-color-ink-soft);
}

.mbr-branches__search-input {
    width: 100%;
    min-width: 0;
    padding: 0;
    background: transparent;
    border: 0;
    outline: none;
    font-size: 12px;
    font-weight: 600;
    color: var(--mbr-color-ink-headline);
}

/* custom.css centres every placeholder */
.mbr-branches__search-input::placeholder {
    text-align: left;
    font-size: inherit;
    color: var(--mbr-color-ink-soft);
    opacity: 1;
}

/* MBR086 info card (design "Corporate HQ" block) */
.mbr-branches__note {
    padding: 16px;
    background: var(--mbr-color-tint);
    border: 1px solid var(--mbr-color-outline);
    border-radius: 20px;
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
    font-size: 12px;
    line-height: 1.4;
    color: var(--mbr-color-ink-secondary);
    white-space: pre-wrap;
}

/* branch cards */
.mbr-branches__list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin: 0;
    padding: 4px 0 0;
    list-style: none;
}

.mbr-branch {
    padding: 16px;
    background: var(--mbr-color-card);
    border: 1px solid var(--mbr-color-tint-dark);
    border-radius: var(--mbr-radius-lg);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-branch__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.mbr-branch__info {
    min-width: 0;
    padding-right: 8px;
}

.mbr-branch__badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 4px;
    padding: 2px 8px;
    border-radius: var(--mbr-radius-full);
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
    background: var(--mbr-color-tint);
    color: var(--mbr-color-ink-soft);
}

.mbr-branch__badge--open {
    background: #E8F5E9;
    color: var(--mbr-color-positive);
}

.mbr-branch__dot {
    width: 6px;
    height: 6px;
    border-radius: var(--mbr-radius-full);
    background: currentColor;
}

.mbr-branch__name {
    font-family: var(--mbr-font-headline) !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: var(--mbr-color-ink-strong);
    overflow-wrap: anywhere;
}

.mbr-branch__address {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin-top: 2px !important;
    font-size: 12px;
    line-height: 16px;
    color: var(--mbr-color-ink-secondary);
    overflow-wrap: anywhere;
}

.mbr-branch__address svg {
    flex: none;
    width: 13px;
    height: 13px;
    margin-top: 1.5px;
}

.mbr-branch__today {
    margin-top: 6px !important;
    font-size: 12px;
    line-height: 16px;
    color: var(--mbr-color-ink-secondary);
}

.mbr-branch__today--strong {
    font-weight: 600;
    color: var(--mbr-color-primary);
}

.mbr-branch__hours-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    padding: 0;
    background: none;
    border: 0;
    font-size: 12px;
    line-height: 16px;
    color: var(--mbr-color-ink-soft);
}

.mbr-branch__hours-toggle svg {
    width: 12px;
    height: 12px;
}

.mbr-branch__order {
    flex: none;
    padding: 8px 16px;
    background: var(--mbr-color-primary);
    border: 0;
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.1);
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: var(--mbr-color-on-primary);
    transition: background-color 0.15s ease;
}

.mbr-branch__order:hover:not(:disabled) {
    background: var(--mbr-color-primary-light);
}

.mbr-branch__order:disabled {
    background: var(--mbr-color-outline);
    box-shadow: none;
    color: var(--mbr-color-ink-soft);
    cursor: not-allowed;
}

/* expanded opening hours */
.mbr-branch__hours {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--mbr-color-tint);
}

.mbr-branch__hours-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 8px;
    padding: 0;
    background: none;
    border: 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--mbr-color-primary);
}

.mbr-branch__hours-head svg {
    width: 14px;
    height: 14px;
}

.mbr-branch__days {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
    padding: 10px;
    background: var(--mbr-color-canvas);
    border-radius: 12px;
    font-size: 11px;
    line-height: 16px;
    color: var(--mbr-color-ink-secondary);
}

.mbr-branch__day {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.mbr-branch__day dt {
    font-weight: 400;
}

.mbr-branch__day dd {
    margin: 0;
    font-weight: 500;
    text-align: right;
    color: var(--mbr-color-ink-headline);
}

.mbr-branch__day--today dt,
.mbr-branch__day--today dd {
    font-weight: 700;
    color: var(--mbr-color-primary);
}

.mbr-branches__empty {
    padding: 32px 16px;
    background: var(--mbr-color-card);
    border: 1px solid var(--mbr-color-tint-dark);
    border-radius: var(--mbr-radius-lg);
    font-size: 13px;
    text-align: center;
    color: var(--mbr-color-ink-muted);
}
</style>
